import { useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import { generatePath } from 'react-router';
import * as yup from 'yup';
import { ContentModel, toNftContent } from '~/components/Content/Content.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { HttpError } from '~/components/Error/HttpError';
import { levelMetaMap, useLevelUpRequirement } from '~/components/Level/Level.hooks';
import { LevelUpValues } from '~/components/LevelUp/LevelUp';
import { LevelUpBottomSheet } from '~/components/LevelUp/Popup';
import { useCitizensByLevel } from '~/components/Membership/Membership.hooks';
import { useBottomSheet } from '~/components/Modal/BottomSheet';
import { UseDosiminMutationOptions, UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useSearchParams } from '~/components/Route';

export type LevelUpSearchParams = {
  level: number;
};
export const levels = Object.values(levelMetaMap).map((levelMeta) => levelMeta.level);

export const levelUpSchema = yup.object({
  level: yup.number().oneOf(levels.slice(0, -1)).required().default(1),
});

export const useLevelUpSearchParams = (schema = levelUpSchema) => {
  const converter = useCallback((params) => schema.castOrDefault(params), [schema]);
  const params = useSearchParams<LevelUpSearchParams>({ converter });
  return params as LevelUpSearchParams;
};

export type LevelUpToken = {
  authUri: string;
  id: number;
};
export type LevelUpTokenValues = {
  returnUri?: string;
} & Pick<LevelUpValues, 'tokenIdList' | 'level'>;
export const levelUpTokenUrl = '/api/citizen/v1/level-up';
export const tfaReturnUri = `${document.location.origin}/auth/tfa/verify`;
export const issueToken = async ({ returnUri = tfaReturnUri, ...values }: LevelUpTokenValues) => {
  const { data } = await axios.post<LevelUpToken & { levelUpId: number }>(levelUpTokenUrl, {
    returnUri,
    ...values,
  });
  return {
    ...data,
    id: data.levelUpId,
  } as LevelUpToken;
};

export const useIssueToken = (
  options: UseMutationOptions<LevelUpToken, HttpError, LevelUpTokenValues>
) =>
  useMutation<LevelUpToken, HttpError, LevelUpTokenValues>({
    mutationFn: issueToken,
    ...options,
  });

export type LevelUpStatus = {
  status: 'REQUESTED' | 'CONFIRM_TRANSACTION' | 'AIRDROP_REQUESTED' | 'COMPLETED' | 'FAILED';
  level: number;
  nftId: number;
} & Partial<ContentModel>;
export const levelUpStatusUrl = '/api/citizen/v1/level-up/:id/status';
export const getLevelUpStatus = async (id: LevelUpToken['id']) => {
  const url = generatePath(levelUpStatusUrl, { id });
  const { data } = await axios.get<LevelUpStatus>(url);

  if (data.status === 'FAILED') throw new BaseError('FAILED', data);

  return data.contentsUrl
    ? ({
        contentsType: 'VIDEO',
        ...data,
        ...toNftContent(data as ContentModel),
      } as LevelUpStatus)
    : data;
};

export const levelUpConfirmUrl = '/api/citizen/v1/level-up/:id/confirm';
export const confirmLevelUpToken = async (id: LevelUpToken['id']) => {
  const url = generatePath(levelUpConfirmUrl, { id });
  await axios.post(url);
};
export const useConfirmLevelUpToken = (
  options?: UseDosiminMutationOptions<unknown, HttpError, LevelUpToken['id']>
) => useMutation(confirmLevelUpToken, options);

export const useLevelUpStatus = (
  id: LevelUpToken['id'],
  options?: UseDosiminQueryOptions<LevelUpStatus, BaseError>
) =>
  useQuery<LevelUpStatus, BaseError>(['levelUp', id], () => getLevelUpStatus(id), {
    initialData: {
      nftId: 0,
      level: 1,
      status: 'REQUESTED',
      originContentsUrl: '',
      contentsType: 'VIDEO',
      contentsUrl: '',
    },
    refetchInterval: 5 * 1000,
    ...options,
  });

export const useLevelUp = (
  options?: UseDosiminMutationOptions<LevelUpStatus, HttpError, LevelUpValues>
) => {
  const { open } = useBottomSheet();
  return useMutation<LevelUpStatus, HttpError, LevelUpValues>({
    mutationFn: async (values: LevelUpValues) => {
      const { confirmed, ...data } = await open(<LevelUpBottomSheet {...values} />, {
        onBackdropClick: false,
        throwOnDismiss: true,
      });
      return data as LevelUpStatus;
    },
    ...options,
  });
};

export const useValidate = (level: number) => {
  const { data: requirement } = useLevelUpRequirement(level);
  const { data: citizensByLevel } = useCitizensByLevel(level);

  return useCallback(
    (values: LevelUpValues) => {
      const { purchase, balance, tokenIdList } = values;

      const errors: Record<string, string> = {};
      if (requirement.purchase.minAmount > purchase) {
        errors.purchase = 'NOT_ENOUGH_PURCHASED';
      }
      if (requirement.balance.minAmount > balance) {
        errors.balance = 'NOT_ENOUGH_DON';
      }
      const nftRequired = requirement.minNftAmount - tokenIdList.length;
      if (citizensByLevel.length < requirement.minNftAmount) {
        errors.tokenIdList = 'NOT_ENOUGH_WALLET_NFTS';
      } else if (nftRequired > 0) {
        errors.tokenIdList = 'NOT_ENOUGH_SELECTED_NFTS';
      } else if (nftRequired < 0) {
        errors.tokenIdList = 'SELECT_OVER_REQUIRED_NFT';
      }
      return errors;
    },
    [citizensByLevel, requirement]
  );
};

export type LevelUpStep = 'TOKEN' | 'TFA' | 'LOADING' | 'COMPLETED';
export const steps: LevelUpStep[] = ['TOKEN', 'TFA', 'LOADING', 'COMPLETED'];
